import { immerable } from 'immer'
import { ReactNode } from 'react'
import Pet from '~models/pet'
import Sentry from '~utils/sentry'

type TLabel = string
type TValue = string | number | boolean

export type SelectBoxData<
  TExtendedLabel extends TLabel = TLabel,
  TExtendedValue extends TValue = TValue,
> = {
  label: TExtendedLabel
  value: TExtendedValue
  icon?: ReactNode
}

type ExtraComponentBaseType = {
  type: string
  datafield: string
  mandatory?: boolean
}

export interface SelectBoxExtraComponentProps {
  root: GroupItem
  options: Omit<GroupItem, 'extra_component' | 'datafield'>[]
  datafield: string
  placeholder: string
  onSelect: (triggeredItem: GroupItem & { datafield: string }) => void
}

export interface TextInputExtraComponentProps {
  root: GroupItem
  datafield: string
  placeholder: string
  onChange: (triggeredItem: GroupItem & { datafield: string }) => void
}

export interface SelectBoxType
  extends Omit<SelectBoxExtraComponentProps, 'root' | 'onSelect'>,
    ExtraComponentBaseType {
  type: 'select'
}

export interface TextFieldType
  extends Omit<TextInputExtraComponentProps, 'root' | 'onChange'>,
    ExtraComponentBaseType {
  type: 'text'
}

export type ExtraComponent = SelectBoxType | TextFieldType

export type GroupItem<T = any> = {
  // 가능하면 사용하지 말 것
  datafield?: string
  value: T
  label?: string
  /* multiple only */
  groupBy?: string
  extra_component?: ExtraComponent
  enabled?: (data: ConsultationData) => boolean
}

type MessageType = {
  sender: 'Owner'
  text: string
  photo_urls: string[]
  video_urls: string[]
}

export interface ConsultationDataDto {
  pet_id: number
  message: MessageType
  checkup: Record<string, Record<string, null> | null>
  metadata: Record<string, string>
}

export class ConsultationData {
  [immerable] = true
  pet: Pet.ILevel1 | null = null
  pet_id: number | null = null
  message: MessageType = {
    sender: 'Owner',
    text: '',
    photo_urls: [],
    video_urls: [],
  }
  checkup: Record<string, Record<string, null> | null> | null = null
  metadata: Record<string, string> = {}

  // Preliminary Checkup 확인 완료한 항목들
  confirmed: string[] = []
  // 추후에 업데이트가 있을 때 Draft ConsultationData 을 초기화하기 위함
  start_at: number = 0
  // 이 상담에서 이미 띄운 응급 안내의 가장 높은 단계 (문진 v2, DRT-7186). 같거나 낮은 단계는 다시 띄우지 않는다
  intake_alert_level: 'urgent' | 'emergency' | null = null
  // 증상 화면에서 Next 를 누를 때 정한 문진 버전. 이후 화면·제출은 이 버전을 따른다 (질문지가 중간에 바뀌어도)
  intake_version: 1 | 2 | null = null
  // 텍스트 질문 중 "I don't know" 버튼으로 'unknown' 이 된 질문 (직접 입력한 "unknown" 과 구분해 화면에 보여 준다)
  intake_text_unknown_ids: string[] = []
  // 프로필 값으로 이미 미리 채운 숫자 질문 (prefill_from). 지운 값을 뒤로 갔을 때 다시 채우지 않는다
  intake_prefilled_ids: string[] = []

  constructor(props: Partial<ConsultationData> = {}) {
    this.pet = props.pet || null
    this.pet_id = props.pet_id || null
    this.checkup = props.checkup || null
    this.confirmed = props.confirmed || []
    this.start_at = props.start_at || Date.now()
    this.intake_alert_level = props.intake_alert_level || null
    this.intake_version = props.intake_version || null
    this.intake_text_unknown_ids = props.intake_text_unknown_ids || []
    this.intake_prefilled_ids = props.intake_prefilled_ids || []

    if (props.message) {
      this.message = Object.assign(this.message, props.message)
    }
  }

  toDto(): ConsultationDataDto {
    if (!this.pet_id || !this.message || !this.checkup) {
      Sentry.captureException(new Error('Invalid consultation data'), {
        extra: {
          consultationData: this,
        },
      })
      throw new Error('Invalid consultation data')
    }

    return {
      pet_id: this.pet_id,
      message: this.message,
      checkup: this.checkup,
      metadata: this.metadata,
    }
  }
}

export type ConsultationProcess = {
  title: string
  description?: string
  component: ComponentType
  enabled?: (data: ConsultationData) => boolean
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isConsultationProcess = (obj: any): obj is ConsultationProcess => {
  return obj.title && obj.component
}

export interface GroupSelectProps {
  root: any
  options: GroupItem[]
  datafield?: string
  mandatory?: boolean
  onSelect: (
    triggeredItem: GroupItem & {
      reset_other_datafields: boolean
    }
  ) => void
  useKeyAsLabel?: boolean
}

type GroupCheckboxOptions = GroupItem[] | { [optionKey: string]: GroupItem[] }
export interface GroupCheckboxProps {
  root: any
  subject: string
  options: GroupCheckboxOptions
  optionKey?: string
  datafield?: string
  onSelect: (
    triggeredItem: GroupItem & {
      reset_other_datafields: boolean
    }
  ) => void
  required?: boolean
}

export interface NumPerDayProps {
  setValue: (item: GroupItem) => void
  options?: undefined
  placeholder: string
}

export interface PeriodProps {
  root: any
  options: Omit<GroupItem, 'extra_component' | 'datafield'>[]
  onSelect: (triggeredItem: GroupItem) => void
}

interface GroupSelectType extends Omit<GroupSelectProps, 'onSelect' | 'root'> {
  type: 'select'
}

interface GroupCheckboxType extends Omit<GroupCheckboxProps, 'onSelect' | 'root' | 'optionKey'> {
  type: 'checkbox'
}

interface GroupPeriodType extends Omit<PeriodProps, 'onSelect' | 'root'> {
  type: 'period'
}

interface GroupNumPerDayType extends Omit<NumPerDayProps, 'setValue'> {
  type: 'num_per_day'
}

export type ComponentType =
  | GroupSelectType
  | GroupCheckboxType
  | GroupPeriodType
  | GroupNumPerDayType

export enum PeriodDatafields {
  unit = 'unit',
  value = 'value',
  still_ongoing = 'still_ongoing',
}
